import { Plus, ShieldAlert } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useCartaoAmareloAllList } from '@/api/queries/cartao-amarelo-queries'
import {
	InspectionDetailsSection,
	ResponsiveFormModal,
} from '@/components/commons'
import { DetailField } from '@/components/commons/detail-field'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardHeader,
	InspectionDetailsHeader,
	InspectionListFilters,
	InspectionPhotoField,
} from '@/components/inspections'
import {
	InspectionSummaryBadge,
	InspectionSummaryCard,
	InspectionSummaryCardBody,
	InspectionSummaryCardField,
	InspectionSummaryCardHeader,
	InspectionSummaryCardLoading,
	InspectionSummaryCardTitle,
} from '@/components/inspections/inspection-summary-card'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter } from '@/components/ui/card'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useAuthStore, useProfileStore } from '@/store'
import type { CartaoAmarelo, CartaoAmareloAllListParams } from '@/types'
import { capitalize } from '@/utils/capitalize'
import { formatDateToDisplay } from '@/utils/formatDate'

const YellowCardDetails = ({ card }: { card: CartaoAmarelo }) => {
	const { t } = useTranslation()
	const colors = INSPECTION_COLORS.sunnyYellow

	return (
		<div className="space-y-5 py-2">
			<InspectionDetailsHeader
				date={formatDateToDisplay(card.date)}
				createdBy={capitalize(card.createdBy.name)}
			/>

			<InspectionDetailsSection>
				<DetailField
					label={t('inspections.common.branch')}
					value={capitalize(card.cr.branchName)}
				/>
				<DetailField
					label={t('inspections.yellowCard.contract')}
					value={capitalize(card.cr.name)}
				/>
				{card.customer.id && (
					<DetailField
						label={t('inspections.common.customer')}
						value={capitalize(card.customer.name)}
					/>
				)}
			</InspectionDetailsSection>

			<InspectionDetailsSection>
				<DetailField
					label={t('inspections.yellowCard.kind')}
					value={card.kind.name}
				/>
				<DetailField
					label={t('inspections.yellowCard.risk')}
					value={card.risk.name}
				/>
			</InspectionDetailsSection>

			<InspectionDetailsSection layout="vertical">
				<DetailField
					label={t('inspections.yellowCard.unsafeDescription')}
					value={card.unsafeDescription}
				/>
				{card.safeDescription && (
					<DetailField
						label={t('inspections.yellowCard.safeDescription')}
						value={card.safeDescription}
					/>
				)}
			</InspectionDetailsSection>

			<InspectionDetailsSection showSeparator={!!card.imagePath}>
				<DetailField
					label={t('inspections.yellowCard.employee')}
					value={capitalize(card.employee.name)}
				/>
				<DetailField
					label={t('inspections.yellowCard.immediateLeader')}
					value={capitalize(card.leader.name)}
				/>
			</InspectionDetailsSection>

			{card.imagePath && (
				<InspectionPhotoField
					attachLabel=""
					altText={t('inspections.yellowCard.photo')}
					preview={card.imagePath}
					onFileChange={() => {}}
					onRemove={() => {}}
					colors={colors}
					showRemoveButton={false}
				/>
			)}
		</div>
	)
}

const yellowCardStyle = {
	accentClassName: 'border-l-kaefer-sunny-yellow',
	badgeClassName:
		'border-kaefer-sunny-yellow/40 bg-kaefer-sunny-yellow/20 text-kaefer-dark-grey',
	badgeLabelKey: 'inspections.types.yellowCard.title',
} as const

export const YellowCardListPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const userRole = useAuthStore((s) => s.user?.role)
	const { defaultBranchId } = useProfileStore()

	// Determina se usuario pode gerenciar filtros de branch
	const canManageBranchFilter =
		userRole === 'ADMIN' ||
		userRole === 'MANAGER' ||
		userRole === 'SSMA_MANAGER'

	const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>()
	const [selectedCrId, setSelectedCrId] = useState<string | undefined>()
	const [selectedCustomerId, setSelectedCustomerId] = useState<
		string | undefined
	>()
	const [search, setSearch] = useState('')
	const { debouncedValue: debouncedSearch, isPending: isSearchPending } =
		useDebouncedValue(search, 600)
	const [selectedCard, setSelectedCard] = useState<CartaoAmarelo | null>(null)
	const [detailsOpen, setDetailsOpen] = useState(false)
	const [cursor, setCursor] = useState<string | undefined>()
	const [cards, setCards] = useState<CartaoAmarelo[]>([])
	const [facets, setFacets] = useState<{
		branches: Array<{ id: string; name: string; count: number }>
		contracts: Array<{ id: string; name: string; count: number }>
		customers: Array<{ id: string; name: string; count: number }>
	}>({
		branches: [],
		contracts: [],
		customers: [],
	})
	const [pageInfo, setPageInfo] = useState<{
		nextCursor: string | null
		hasNext: boolean
	}>({
		nextCursor: null,
		hasNext: false,
	})

	const shouldShowCustomerFilter =
		facets.customers.length > 0 || Boolean(selectedCustomerId)

	const queryParams = useMemo<CartaoAmareloAllListParams>(
		() => ({
			// Para usuarios nao-admin, usa automaticamente a branch do perfil
			branchId: canManageBranchFilter ? selectedBranchId : defaultBranchId,
			crId: selectedCrId,
			customerId: shouldShowCustomerFilter ? selectedCustomerId : undefined,
			q: debouncedSearch.trim() || undefined,
			cursor,
		}),
		[
			canManageBranchFilter,
			selectedBranchId,
			defaultBranchId,
			selectedCrId,
			selectedCustomerId,
			shouldShowCustomerFilter,
			debouncedSearch,
			cursor,
		],
	)

	const {
		data: listData,
		isLoading,
		isFetching,
	} = useCartaoAmareloAllList(queryParams)

	useEffect(() => {
		if (!listData) return

		setFacets({
			branches: listData.facets.branches ?? [],
			contracts: listData.facets.contracts ?? [],
			customers: listData.facets.customers ?? [],
		})
		setPageInfo(listData.pageInfo)

		setCards((previous) => {
			if (!cursor) {
				return listData.items
			}

			const existingIds = new Set(previous.map((card) => card.id))
			const newItems = listData.items.filter(
				(card) => !existingIds.has(card.id),
			)
			return [...previous, ...newItems]
		})
	}, [listData, cursor])

	const resetListState = () => {
		setCursor(undefined)
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	const handleSearchChange = (value: string) => {
		setSearch(value)
		resetListState()
	}

	const handleBranchChange = (value: string) => {
		setSelectedBranchId(value || undefined)
		setSelectedCrId(undefined)
		setSelectedCustomerId(undefined)
		resetListState()
	}

	const handleContractChange = (value: string) => {
		setSelectedCrId(value || undefined)
		setSelectedCustomerId(undefined)
		resetListState()
	}

	const handleCustomerChange = (value: string) => {
		setSelectedCustomerId(value || undefined)
		resetListState()
	}

	useEffect(() => {
		if (shouldShowCustomerFilter || !selectedCustomerId) return
		setSelectedCustomerId(undefined)
	}, [shouldShowCustomerFilter, selectedCustomerId])

	const handleBack = () => {
		navigate(ROUTES.INSPECTIONS.INDEX)
	}

	const handleNewCard = () => {
		navigate(ROUTES.INSPECTIONS.YELLOW_CARD.NEW)
	}

	const handleViewCard = (card: CartaoAmarelo) => {
		setSelectedCard(card)
		setDetailsOpen(true)
	}

	const handleLoadMore = () => {
		if (!pageInfo.hasNext || !pageInfo.nextCursor || isFetching) return
		setCursor(pageInfo.nextCursor)
	}

	const colors = INSPECTION_COLORS.sunnyYellow
	const hasMore = pageInfo.hasNext && Boolean(pageInfo.nextCursor)
	const isLoadingMore = isFetching && Boolean(cursor)
	const footerCountLabel =
		cards.length === 1
			? t('inspections.yellowCard.countSingular')
			: t('inspections.yellowCard.countPlural')
	const footerText = `${hasMore ? `${cards.length}+` : cards.length} ${footerCountLabel}`

	const filterSelects = [
		// Filtro de Branch: visivel apenas para ADMIN, MANAGER e SSMA_MANAGER
		...(canManageBranchFilter
			? [
					{
						id: 'yellow-card-filter-branch',
						label: t('inspections.common.branch'),
						value: selectedBranchId,
						onChange: handleBranchChange,
						options: facets.branches.map((branch) => ({
							value: branch.id,
							label: branch.name,
							count: branch.count,
						})),
						allLabel: t('inspections.yellowCard.filterAll'),
					},
				]
			: []),
		{
			id: 'yellow-card-filter-contract',
			label: t('inspections.yellowCard.filterByContract'),
			value: selectedCrId,
			onChange: handleContractChange,
			options: facets.contracts.map((contract) => ({
				value: contract.id,
				label: contract.name,
				count: contract.count,
			})),
			allLabel: t('inspections.yellowCard.filterAll'),
		},
		...(shouldShowCustomerFilter
			? [
					{
						id: 'yellow-card-filter-customer',
						label: t('inspections.yellowCard.filterByCustomer'),
						value: selectedCustomerId,
						onChange: handleCustomerChange,
						options: facets.customers.map((customer) => ({
							value: customer.id,
							label: customer.name,
							count: customer.count,
						})),
						allLabel: t('inspections.yellowCard.filterAll'),
					},
				]
			: []),
	]
	const renderYellowCard = (card: CartaoAmarelo) => (
		<InspectionSummaryCard
			key={card.id}
			accentClassName={yellowCardStyle.accentClassName}
			onClick={() => handleViewCard(card)}
		>
			<InspectionSummaryCardBody>
				<InspectionSummaryCardHeader
					badge={
						<InspectionSummaryBadge
							label={t(yellowCardStyle.badgeLabelKey)}
							className={yellowCardStyle.badgeClassName}
						/>
					}
					topMeta={
						<span className="text-xs text-muted-foreground">
							{formatDateToDisplay(card.date)}
						</span>
					}
				/>

				<InspectionSummaryCardTitle>
					{capitalize(card.unsafeDescription || card.cr.name)}
				</InspectionSummaryCardTitle>

				<div className="flex items-center justify-between gap-2 text-xs">
					<div className="min-w-0 flex-1 space-y-2">
						<InspectionSummaryCardField
							label="Criado por: "
							value={capitalize(card.createdBy.name)}
						/>
						<InspectionSummaryCardField
							label="Contrato: "
							value={capitalize(card.cr.name)}
						/>
					</div>
				</div>
			</InspectionSummaryCardBody>
		</InspectionSummaryCard>
	)

	return (
		<>
			<InspectionCard colors={colors}>
				<InspectionCardHeader
					icon={<ShieldAlert className="size-5" />}
					title={t('inspections.types.yellowCard.title')}
					subtitle={t('inspections.yellowCard.listSubtitle')}
					colors={colors}
					onBack={handleBack}
					actions={
						<Button
							onClick={handleNewCard}
							className="w-full gap-2 bg-kaefer-sunny-yellow font-semibold text-kaefer-dark-grey hover:bg-kaefer-sunny-yellow/90 sm:w-auto"
						>
							<Plus className="size-4" />
							{t('inspections.yellowCard.newCard')}
						</Button>
					}
				/>
				<CardContent className="flex-1 space-y-4">
					<InspectionListFilters
						searchValue={search}
						onSearchChange={handleSearchChange}
						selects={filterSelects}
					/>

					{isSearchPending && cards.length > 0 && (
						<InspectionSummaryCardLoading
							accentClassName={yellowCardStyle.accentClassName}
						/>
					)}

					{isLoading && cards.length === 0 ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{Array.from({ length: 6 }, (_, index) => (
								<InspectionSummaryCardLoading
									key={`yellow-card-loading-${index}`}
									accentClassName={yellowCardStyle.accentClassName}
								/>
							))}
						</div>
					) : cards.length > 0 ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{cards.map((card) => renderYellowCard(card))}
						</div>
					) : (
						<div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-kaefer-light-silver py-16">
							<ShieldAlert className="size-10 text-kaefer-silver" />
							<p className="mt-3 text-sm text-muted-foreground">
								{t('inspections.yellowCard.emptyList')}
							</p>
						</div>
					)}

					{hasMore && cards.length > 0 && (
						<div className="flex justify-center">
							<Button
								variant="outline"
								onClick={handleLoadMore}
								disabled={isLoadingMore}
								className="gap-2"
							>
								{isLoadingMore ? (
									<>
										<div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
										{t('inspections.yellowCard.loadingMore')}
									</>
								) : (
									<>
										<Plus className="size-4" />
										{t('inspections.yellowCard.loadMore')}
									</>
								)}
							</Button>
						</div>
					)}
				</CardContent>
				<CardFooter className="mt-4 border-t border-kaefer-sunny-yellow/30 bg-kaefer-sunny-yellow/5 py-3">
					<p className="w-full text-xs text-muted-foreground">{footerText}</p>
				</CardFooter>
			</InspectionCard>
			<ResponsiveFormModal
				open={detailsOpen}
				onOpenChange={setDetailsOpen}
				title={t('inspections.yellowCard.details')}
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-3xl"
			>
				{selectedCard && <YellowCardDetails card={selectedCard} />}
			</ResponsiveFormModal>
		</>
	)
}
